import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import Card from "../../../../components/Card";
import PasswordPairWithRules from "../../../../components/PasswordPairWithRules";
import Input from "../../../../components/Input";
import { UIButton } from "../../../../components/ui";
import { changeCompanyPasswordRequest } from "../../../../store/Auth/actions";
import {
  passwordHasCharsetRules,
  passwordHasMinLength,
} from "../../../../components/PasswordPairWithRules/passwordRules";
import { PASSWORD_REGEX } from "../../../../utils/Patterns";

import "./_Security.scss";

const initialValues = { oldPassword: "", newPassword: "", confirmPassword: "" };

const SECURITY_FORM_ID = "company-security-change-password";

const Security = () => {
  const lang = useSelector((state) => state.locale.lang);
  const dispatch = useDispatch();
  const { changePassword: t, resetPassword: tReset, shared } = messages[lang];
  const subtitle = t.security.companyPortalSecuritySubtitle;

  const changePasswordSettledSeq = useSelector(
    (state) => state.auth.changePasswordSettledSeq ?? 0
  );
  const lastHandledSettledSeq = useRef(changePasswordSettledSeq);

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    setSubmitting: setFormSubmitting,
    isValid,
    dirty,
    isSubmitting,
  } = useFormik({
    validateOnMount: true,
    initialValues: { ...initialValues },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("oldPasswordRequired"),
      newPassword: Yup.string()
        .required("newPasswordRequired")
        .matches(PASSWORD_REGEX, ""),
      confirmPassword: Yup.string()
        .required("confirmNewPasswordRequired")
        .oneOf([Yup.ref("newPassword")], "passwordIsNotMatched"),
    }),
    onSubmit: (formValues, { setSubmitting }) => {
      setSubmitting(true);
      dispatch(
        changeCompanyPasswordRequest({
          from: "change",
          data: {
            old_password: formValues.oldPassword,
            new_password: formValues.newPassword,
          },
        })
      );
    },
  });

  useEffect(() => {
    if (changePasswordSettledSeq === lastHandledSettledSeq.current) {
      return;
    }
    lastHandledSettledSeq.current = changePasswordSettledSeq;
    setFormSubmitting(false);
  }, [changePasswordSettledSeq, setFormSubmitting]);

  const lenOk = passwordHasMinLength(values.newPassword);
  const charsetOk = passwordHasCharsetRules(values.newPassword);

  const showConfirmMatch =
    touched.confirmPassword &&
    values.confirmPassword &&
    values.newPassword === values.confirmPassword &&
    !errors.confirmPassword;

  const footer = (
    <UIButton
      type="submit"
      form={SECURITY_FORM_ID}
      variant="primary"
      size="large"
      disabled={!(isValid && dirty) || isSubmitting}
      loading={isSubmitting}
    >
      {t.updatePassword}
    </UIButton>
  );

  return (
    <div className="security-container">
      <div className="mt-1">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.companySettingsProfile,
              label: shared.settings,
            },
            {
              path: "",
              label: t.security.title,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
      </div>
      <header className="security-container__header">
        <p className="security-container__subtitle text-body-sm">{subtitle}</p>
      </header>
      <div className="security-container__content">
        <Card
          title={t.changePassword}
          subtitle={t.security.keepYourAccountSecure}
          footer={footer}
          classNames={{ root: "security-container__card" }}
        >
          <form
            id={SECURITY_FORM_ID}
            className="security-container__form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="security-container__old-password">
              <Input
                id="security-old-password"
                name="oldPassword"
                type="password"
                label={t.resetPage.oldPassword}
                placeholder={t.placeholders.oldPassword}
                required
                value={values.oldPassword}
                onChange={(value) => {
                  setFieldTouched("oldPassword", true);
                  setFieldValue("oldPassword", value);
                }}
                onBlur={handleBlur}
                isInputHasErr={Boolean(
                  touched.oldPassword && errors.oldPassword
                )}
                errMsg={errors.oldPassword}
                isPasswordInput
                inputWrapperClass="mb-0"
                labelClassName="font-medium mb-2"
              />
            </div>

            <PasswordPairWithRules
              passwordId="security-new-password"
              confirmId="security-confirm-new-password"
              passwordName="newPassword"
              confirmName="confirmPassword"
              passwordLabel={t.labels.newPassword}
              confirmLabel={t.labels.confirmNewPassword}
              passwordPlaceholder={t.placeholders.newPassword}
              confirmPlaceholder={t.placeholders.confirmNewPassword}
              passwordValue={values.newPassword}
              confirmValue={values.confirmPassword}
              onPasswordChange={(value) => {
                setFieldTouched("newPassword", true);
                setFieldValue("newPassword", value);
              }}
              onConfirmChange={(value) => {
                setFieldTouched("confirmPassword", true);
                setFieldValue("confirmPassword", value);
              }}
              onBlur={handleBlur}
              passwordRequired
              passwordError={errors.newPassword}
              confirmError={errors.confirmPassword}
              passwordTouched={touched.newPassword}
              confirmTouched={touched.confirmPassword}
              rules={[
                {
                  label: tReset.step2.rules.length,
                  satisfied: values.newPassword.length > 0 ? lenOk : undefined,
                },
                {
                  label: tReset.step2.rules.complexity,
                  satisfied:
                    values.newPassword.length > 0 ? charsetOk : undefined,
                },
              ]}
              showExternalConfirmMatch
              confirmMatchVisible={Boolean(showConfirmMatch)}
              confirmMatchAriaLabel={tReset.step2.confirmMatchAria}
            />
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Security;
